import React, { useState, useEffect } from 'react';
import { api } from '../../api/client';
import { useNavigate, Link } from 'react-router-dom';
import { Search, Filter, Star, Download, ChevronRight, Package, TrendingUp, Code, Grid } from 'lucide-react';
import MarketplaceLayout from '../../layouts/MarketplaceLayout';
import LandingPage from './LandingPage';
export default function MarketplaceLanding() {
    const [apps, setApps] = useState([]);
    const [filteredApps, setFilteredApps] = useState([]);
    const [loading, setLoading] = useState(false); // Set to false - no initial load needed
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('all');
    const [categories, setCategories] = useState(['all']);
    const [currentUser, setCurrentUser] = useState(null);
    const navigate = useNavigate();

    // Only fetch current user, not all apps (landing page doesn't display them)
    useEffect(() => {
        fetchCurrentUser();
    }, []);

    // Remove automatic filtering since we don't pre-load apps
    // Search is handled by MarketplaceSearch component

    const fetchCurrentUser = async () => {
    try {
        const token = localStorage.getItem('token');
        if (!token) return;
        
        // Create a simple endpoint to get current user info
        const response = await api.get('/api/user/profile');
        setCurrentUser(response.data);
    } catch (error) {
        // Silently fail - user might not be logged in
        console.log('User not authenticated');
    }
};

    // Keep this function but don't call it on mount
    const fetchMarketplaceApps = async () => {
        try {
            const response = await api.get('/api/marketplace/apps');
            
            // Add user_id to each app from the response
            const appsWithUserInfo = response.data.map(app => ({
                ...app,
                user_id: app.user_id // Make sure this field is included in the API response
            }));
            
            setApps(appsWithUserInfo);
            
            // Extract unique categories
            const allCategories = appsWithUserInfo
                .map(app => app.category)
                .filter(Boolean);
            const uniqueCategories = ['all', ...new Set(allCategories)];
            setCategories(uniqueCategories);
        } catch (error) {
            console.error('Failed to fetch apps:', error);
        } finally {
            setLoading(false);
        }
    };

    const filterApps = () => {
        let filtered = [...apps];

        // Search filter
        if (searchTerm) {
            filtered = filtered.filter(app =>
                app.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.short_description?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                app.description?.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Category filter
        if (selectedCategory !== 'all') {
            filtered = filtered.filter(app => app.category === selectedCategory);
        }

        setFilteredApps(filtered);
    };

    const handleAppClick = (app) => {
        const identifier = app.slug || app.id;
        
        // If it's the user's own app, navigate to the developer portal
        if (currentUser && app.user_id === currentUser.id) {
            navigate(`/dashboard/apps`);
        } else {
            navigate(`/marketplace/app/${identifier}`);
        }
    };

    if (loading) {
        return (
            <MarketplaceLayout>
                <div className="flex justify-center items-center h-64">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                </div>
            </MarketplaceLayout>
        );
    }

    return (
       
        <MarketplaceLayout>
            
            <LandingPage />

            {/* COMMENTED OUT: Dentulu App Marketplace Section */}
            {/* 
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="text-center mb-12">
                    <h1 className="text-4xl font-bold text-gray-900 mb-4">
                        Dentulu App Marketplace
                    </h1>
                    <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
                        Discover powerful applications that integrate seamlessly with your Dentulu ecosystem
                    </p>
                    
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 max-w-2xl mx-auto">
                        <h3 className="text-lg font-semibold text-blue-900 mb-2">
                            Are you a developer?
                        </h3>
                        <p className="text-blue-700 mb-4">
                            Build and list your dental software applications on our marketplace
                        </p>
                        <Link 
                            to="/portal" 
                            className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
                        >
                            <Code className="w-5 h-5" />
                            Access Developer Portal
                        </Link>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-sm p-4 mb-8">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1 relative">
                            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                            <input
                                type="text"
                                placeholder="Search apps..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>
                        <div className="flex items-center gap-2">
                            <Filter className="text-gray-400 w-5 h-5" />
                            <select
                                value={selectedCategory}
                                onChange={(e) => setSelectedCategory(e.target.value)}
                                className="px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                {categories.map(cat => (
                                    <option key={cat} value={cat}>
                                        {cat.charAt(0).toUpperCase() + cat.slice(1)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                </div>

                <div className="flex justify-center gap-4 mb-8">
                    <Link 
                        to="/marketplace/categories"
                        className="inline-flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-700 px-6 py-3 rounded-lg transition-colors"
                    >
                        <Grid className="w-5 h-5" />
                        Browse by Category
                    </Link>
                </div>

                {filteredApps.length > 0 && (
                    <div className="mb-12">
                        <h2 className="text-2xl font-semibold mb-6">Featured Apps</h2>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                            {filteredApps.slice(0, 3).map(app => (
                                <FeaturedAppCard 
                                    key={app.id} 
                                    app={app} 
                                    onClick={() => handleAppClick(app)}
                                    isOwnApp={currentUser && app.user_id === currentUser.id}
                                />
                            ))}
                        </div>
                    </div>
                )}

                <div>
                    <h2 className="text-2xl font-semibold mb-6">All Applications</h2>
                    {filteredApps.length === 0 ? (
                        <div className="text-center py-12">
                            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                            <p className="text-gray-600">No apps found matching your criteria</p>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {filteredApps.map(app => (
                                <AppCard 
                                    key={app.id} 
                                    app={app} 
                                    onClick={() => handleAppClick(app)}
                                    isOwnApp={currentUser && app.user_id === currentUser.id}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </div>
            */}
        </MarketplaceLayout>
    );
}

// Featured App Card Component
const FeaturedAppCard = ({ app, onClick, isOwnApp }) => {
    return (
        <div 
            onClick={onClick}
            className="bg-gradient-to-br from-blue-50 to-indigo-100 p-6 rounded-lg shadow-md hover:shadow-xl transition-all cursor-pointer border border-blue-200"
        >
            <div className="flex items-start justify-between mb-4">
                {app.icon_url ? (
                    <img 
                        src={app.icon_url} 
                        alt={app.name} 
                        className="w-20 h-20 rounded-xl object-cover"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2UyZThmMCIvPjx0ZXh0IHRleHQtYW5jaG9yPSJtaWRkbGUiIHg9IjUwIiB5PSI1MCIgc3R5bGU9ImZpbGw6IzY0NzQ4Yjtmb250LXdlaWdodDpib2xkO2ZvbnQtc2l6ZTozNnB4O2ZvbnQtZmFtaWx5OkFyaWFsLHNhbnMtc2VyaWYiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPkE8L3RleHQ+PC9zdmc+';
                        }}
                    />
                ) : (
                    <div className="w-20 h-20 bg-blue-600 rounded-xl flex items-center justify-center">
                        <Package className="w-10 h-10 text-white" />
                    </div>
                )}
                <span className="bg-yellow-400 text-yellow-900 text-xs px-2 py-1 rounded-full font-semibold">
                    Featured
                </span>
            </div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">{app.name}</h3>
            <p className="text-gray-700 text-sm mb-4 line-clamp-2">
                {app.short_description || app.description}
            </p>
            <div className="flex items-center justify-between">
                <div className="font-semibold">
                    {isOwnApp ? (
                        <span className="text-gray-600">Your App</span>
                    ) : (
                        app.pricing_model === 'free' ? (
                            <span className="text-green-600">Free</span>
                        ) : (
                            <span className="text-gray-900">${app.price}</span>
                        )
                    )}
                </div>
                <span className="text-blue-700 font-medium">
                    {isOwnApp ? 'Manage →' : 'View Details →'}
                </span>
            </div>
        </div>
    );
};

// Regular App Card Component
const AppCard = ({ app, onClick, isOwnApp }) => {
    return (
        <div 
            onClick={onClick}
            className="bg-white p-6 rounded-lg shadow hover:shadow-lg transition-all cursor-pointer group"
        >
            <div className="flex items-start justify-between mb-4">
                {app.icon_url ? (
                    <img 
                        src={app.icon_url} 
                        alt={app.name} 
                        className="w-14 h-14 rounded-lg object-cover"
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                        }}
                    />
                ) : null}
                <div 
                    className="w-14 h-14 bg-gray-200 rounded-lg flex items-center justify-center"
                    style={{ display: app.icon_url ? 'none' : 'flex' }}
                >
                    <Package className="w-7 h-7 text-gray-500" />
                </div>
                <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 transition-colors" />
            </div>
            
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{app.name}</h3>
            <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {app.short_description || app.description}
            </p>
            
            {app.category && (
                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded-full">
                    {app.category}
                </span>
            )}
            
            <div className="flex items-center justify-between text-sm mt-4">
                <div className="font-semibold text-gray-900">
                    {isOwnApp ? (
                        <span className="text-gray-500">Your App</span>
                    ) : (
                        app.pricing_model === 'free' ? (
                            <span className="text-green-600">Free</span>
                        ) : (
                            <span>${app.price} {app.price_period && `/ ${app.price_period}`}</span>
                        )
                    )}
                </div>
                <span className="text-blue-600 group-hover:underline">
                    {isOwnApp ? 'Manage →' : 'View Details →'}
                </span>
            </div>
        </div>
    );
};